/**
 * FE shape mirror of the gateway's `ArgusContextSchema`. Kept in sync with
 * `be/argus-ts/src/argus-context.ts` — the gateway validates the POST body
 * against its TypeBox schema and rejects mismatches, so this file and that
 * one MUST stay structurally aligned.
 */

import { matchRouteContext } from "./route-registry";

export type ArgusBehaviorMode = "interactive" | "autonomous";

export type ArgusTriggerSource = "chat" | "sidebar" | "cron" | "event" | "slack";

export interface ArgusContextResource {
  type: string;
  id: string;
  url?: string;
}

export interface ArgusSessionContext {
  behavior_mode: ArgusBehaviorMode;
  trigger_source: ArgusTriggerSource;
  preload_skill: string | null;
  // Soft hint for interactive sessions. Derived from the current
  // pathname in `buildDefaultArgusContext` — e.g. a failed-run detail
  // page suggests `argus-debug`. The gateway appends this to the
  // "Current Session Context" block; the agent is free to ignore it.
  // `null` means no hint (e.g. the /argus chat page).
  suggested_skill?: string | null;
  context_resource: ArgusContextResource | null;
}

// ---------------------------------------------------------------------------
// URL → context helpers
// ---------------------------------------------------------------------------

/**
 * Infer a `context_resource` from the current `location.pathname` using the
 * shared Argus route registry. Falls back to the first (param, value) pair
 * when the route doesn't expose a conventional `<thing>Id` field.
 *
 * Minimal by design: later plans will wire richer metadata (resource names,
 * enriched URLs) through the provider layer.
 */
export function extractResourceFromUrl(pathname: string): ArgusContextResource | null {
  const match = matchRouteContext(pathname);
  if (!match) return null;

  // Convention: registry keys are "<resource>_<detail-ish>" — strip the
  // suffix so `test_detail` → `test`, `test_run_detail` → `test_run`,
  // `browser_session_detail` → `browser_session`. Keeps the FE helper in
  // lockstep with how the agent refers to resources (singular noun).
  const type = match.key.replace(/_detail$/, "").replace(/_list$/, "");

  // Prefer an explicit `<type>Id` entry if present; otherwise use the first
  // available param so e.g. `issue_detail` with `{issueId}` still resolves.
  const params = match.params;
  const preferredKey = `${type}Id`;
  const chosenId = params[preferredKey] ?? Object.values(params).find((v) => v.length > 0) ?? null;
  if (!chosenId) return null;

  return { type, id: chosenId };
}

/**
 * Choose a sensible default `trigger_source` based on the current path.
 * `/argus` itself is the chat page; every other path hits the gateway via
 * the global sidebar.
 */
export function deriveTriggerSource(pathname: string): ArgusTriggerSource {
  return pathname.startsWith("/argus") ? "chat" : "sidebar";
}

/**
 * Infer a soft `suggested_skill` hint from the current pathname. The
 * gateway appends this to the "Current Session Context" block for
 * interactive sessions; the agent is free to ignore it.
 *
 * Mapping rules (kept conservative — a missing hint is better than a
 * misleading one):
 *   - `/argus`                      → null  (standalone chat, no scope)
 *   - `/tests/new`                  → "argus-author" (net-new test creation)
 *   - `/tests/:testId`              → "argus-debug"  (existing test context)
 *   - `/runs/:runId`                → "argus-debug"  (run debugging is the common ask)
 *   - `/app-configs/:appConfigId`   → "argus-explore" (whole-app exploration)
 *   - everything else               → null  (no hint — agent picks)
 */
export function deriveSuggestedSkill(pathname: string): string | null {
  if (pathname.startsWith("/argus")) return null;
  if (pathname === "/tests/new" || pathname.startsWith("/tests/new/")) {
    return "argus-author";
  }
  if (/^\/tests\/[^/]+/.test(pathname)) return "argus-debug";
  if (/^\/runs\/[^/]+/.test(pathname)) return "argus-debug";
  if (/^\/app-configs\/[^/]+/.test(pathname)) return "argus-explore";
  return null;
}

/**
 * Build the default FE-side argusContext sent with `/session/cmds/start`.
 */
export function buildDefaultArgusContext(pathname: string): ArgusSessionContext {
  return {
    behavior_mode: "interactive",
    trigger_source: deriveTriggerSource(pathname),
    preload_skill: null,
    suggested_skill: deriveSuggestedSkill(pathname),
    context_resource: extractResourceFromUrl(pathname),
  };
}
